import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import {
  RegistrationErrorService,
  ErrorMessage,
} from '@core/services/registration-error.service';

/**
 * RegistrationErrorsComponent
 *
 * Displays error messages, warnings, and success notifications for the registration flow.
 * Can be included in any registration page to show centralized notifications.
 */
@Component({
  selector: 'app-registration-errors',
  standalone: true,
  imports: [],
  template: `
    @if (errors.length > 0) {
      <div class="registration-errors-container">
        @for (error of errors; track trackByErrorId($index, error)) {
          <div
        [class]="
          'alert alert-' +
          getAlertClass(error.type) +
          ' alert-dismissible fade show'
        "
            role="alert"
            >
            <div class="d-flex">
              <i [class]="'bi ' + getIcon(error.type) + ' flex-shrink-0 me-2'"></i>
              <div class="flex-grow-1">
                @if (getTitle(error.type)) {
                  <div class="fw-bold">
                    {{ getTitle(error.type) }}
                  </div>
                }
                <div>{{ error.message }}</div>
              </div>
              <button
                type="button"
                class="btn-close"
                (click)="removeError(error.id)"
                [attr.aria-label]="'Close ' + error.type + ' message'"
              ></button>
            </div>
          </div>
        }
      </div>
    }
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .registration-errors-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1055;
        max-width: 400px;
      }

      .alert {
        margin-bottom: 0.5rem;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        border: none;
        border-radius: 8px;
      }

      .alert-success {
        background-color: var(--bs-success);
        color: white;
      }

      .alert-danger {
        background-color: var(--bs-danger);
        color: white;
      }

      .alert-warning {
        background-color: var(--bs-warning);
        color: var(--bs-dark);
      }

      .alert-info {
        background-color: var(--bs-info);
        color: white;
      }

      .btn-close {
        filter: brightness(0) invert(1);
      }

      .alert-warning .btn-close {
        filter: brightness(0);
      }

      @media (max-width: 768px) {
        .registration-errors-container {
          left: 20px;
          right: 20px;
          max-width: none;
        }
      }
    `,
  ],
})
export class RegistrationErrorsComponent implements OnInit, OnDestroy {
  private errorService = inject(RegistrationErrorService);
  private destroy$ = new Subject<void>();

  errors: ErrorMessage[] = [];

  ngOnInit(): void {
    this.errorService.errors$
      .pipe(takeUntil(this.destroy$))
      .subscribe((errors) => {
        this.errors = errors;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Remove specific error message
   * @param id Error message ID
   */
  removeError(id: string): void {
    this.errorService.removeError(id);
  }

  /**
   * Get Bootstrap alert class for error type
   * @param type Error message type
   * @returns Bootstrap alert class
   */
  getAlertClass(type: string): string {
    switch (type) {
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'success':
        return 'success';
      default:
        return 'primary';
    }
  }

  /**
   * Get icon for error type
   * @param type Error message type
   * @returns Bootstrap icon class
   */
  getIcon(type: string): string {
    switch (type) {
      case 'error':
        return 'bi-exclamation-triangle-fill';
      case 'warning':
        return 'bi-exclamation-triangle';
      case 'info':
        return 'bi-info-circle-fill';
      case 'success':
        return 'bi-check-circle-fill';
      default:
        return 'bi-info-circle';
    }
  }

  /**
   * Get title for error type
   * @param type Error message type
   * @returns Title string or null
   */
  getTitle(type: string): string | null {
    switch (type) {
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'success':
        return 'Success';
      default:
        return null;
    }
  }

  /**
   * Track function for ngFor performance
   * @param index Array index
   * @param error Error message
   * @returns Unique identifier
   */
  trackByErrorId(index: number, error: ErrorMessage): string {
    return error.id;
  }
}
